import { useState, useEffect, useCallback, useRef } from 'react';
import useDebounce from '../hooks/useDebounce';
import {
    Box, Heading, VStack, HStack, FormControl, FormLabel, Input, Textarea, Select, Switch,
    Button, IconButton, Icon, useToast, Spinner, Center, Checkbox, CheckboxGroup, Text, Divider, SimpleGrid, Badge,
    Slider, SliderTrack, SliderFilledTrack, SliderThumb,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
} from '@chakra-ui/react';
import { FiChevronLeft, FiImage, FiLink } from 'react-icons/fi';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { useNavigate, useParams } from 'react-router-dom';
import { authAPI, resolveImageUrl } from '../services/api';
import PhonePreview from '../components/PhonePreview';
import LoadingOverlay from '../components/LoadingOverlay';
import getCroppedBlob from '../utils/cropImage';

const DEPTS = [
    { value: 'AfterSchool', label: '全科班' },
    { value: 'English', label: '英文部' },
    { value: 'Talent', label: '才藝部' },
];
const GRADES = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
const COVER_ASPECT = 358 / 160; // 家長端 Banner 比例

const AnnouncementEditPage = () => {
    const { id } = useParams();
    const isEdit = !!id;
    const navigate = useNavigate();
    const toast = useToast();

    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [saveStatusText, setSaveStatusText] = useState('');
    const [classes, setClasses] = useState([]);

    // 送出前選好但還沒真正上傳的圖片：key 是本機 blob 網址、value 是原始 File/Blob
    const pendingFilesRef = useRef(new Map());

    const [form, setForm] = useState({
        title: '', summary: '', category: '', content: '',
        cover_image_url: '', cover_position: '50% 50%',
        is_emergency: false, is_pinned: false,
        push_on_publish: true, expires_at: '',
    });
    const [scope, setScope] = useState('all');          // all / grade / class / department
    const [scopeValues, setScopeValues] = useState([]);
    const [publishMode, setPublishMode] = useState('draft'); // draft / now / scheduled
    const [publishAt, setPublishAt] = useState('');

    // 封面裁切跳窗
    const [cropSrc, setCropSrc] = useState(null);
    const [cropOpen, setCropOpen] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [cropping, setCropping] = useState(false);

    useEffect(() => {
        authAPI.getClassesLite().then((res) => setClasses(res.data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (!isEdit) return;
        authAPI.announcements.getDetail(id).then((res) => {
            const a = res.data;
            setContentInput(a.content || '');
            setForm({
                title: a.title || '', summary: a.summary || '', category: a.category || '',
                content: a.content || '',  cover_image_url: a.cover_image_url || '',
                cover_position: a.cover_position || '50% 50%',
                is_emergency: !!a.is_emergency, is_pinned: !!a.is_pinned,
                push_on_publish: !!a.push_on_publish,
                expires_at: a.expires_at ? a.expires_at.slice(0, 16) : '',
            });
            const ts = a.targets || [];
            if (ts.length === 0 || ts.some((t) => t.target_type === 'all')) {
                setScope('all'); setScopeValues([]);
            } else {
                const type = ts[0].target_type;
                setScope(type);
                setScopeValues(ts.filter((t) => t.target_type === type).map((t) => String(t.target_value)));
            }
            setPublishMode(a.status === 'published' ? 'now' : (a.status === 'scheduled' ? 'scheduled' : 'draft'));
            if (a.publish_at) setPublishAt(a.publish_at.slice(0, 16));
        }).catch(() => {
            toast({ title: '載入失敗', status: 'error' });
            navigate('/announcements');
        }).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // 選檔 → 開裁切跳窗（用本機 object URL，無 CORS）
    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // 允許重新選同一檔
        if (!file) return;
        setCropSrc(URL.createObjectURL(file));
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        setCropOpen(true);
    };

    const onCropComplete = useCallback((_, areaPixels) => setCroppedAreaPixels(areaPixels), []);

    const closeCrop = () => {
        setCropOpen(false);
        if (cropSrc) URL.revokeObjectURL(cropSrc);
        setCropSrc(null);
        setCroppedAreaPixels(null);
    };

    // 套用裁切 → 先只存本機預覽，真正上傳延後到送出時才做（避免草稿/重裁產生孤兒圖片）
    const applyCrop = async () => {
        if (!cropSrc || !croppedAreaPixels) return;
        setCropping(true);
        try {
            const blob = await getCroppedBlob(cropSrc, croppedAreaPixels);
            const localUrl = URL.createObjectURL(blob);
            if (form.cover_image_url?.startsWith('blob:')) {
                URL.revokeObjectURL(form.cover_image_url);
                pendingFilesRef.current.delete(form.cover_image_url);
            }
            pendingFilesRef.current.set(localUrl, blob);
            setForm((f) => ({ ...f, cover_image_url: localUrl, cover_position: '50% 50%' }));
            closeCrop();
        } catch (err) {
            toast({ title: '裁切失敗', status: 'error', description: err?.message || '' });
        } finally {
            setCropping(false);
        }
    };

    // #7 內文插入圖片（先只存本機預覽，真正上傳延後到送出時才做）
    const [contentInput, setContentInput] = useState('');
    const debouncedContent = useDebounce(contentInput, 300);
    const insertBodyImage = (file) => {
        if (!file) return;
        const localUrl = URL.createObjectURL(file);
        pendingFilesRef.current.set(localUrl, file);
        const newContent = `${contentInput}\n\n![](${localUrl})\n`;
        setContentInput(newContent);
        setForm((f) => ({ ...f, content: newContent }));
        toast({ title: '圖片已插入內文（送出時才會真正上傳）', status: 'success', duration: 2000 });
    };
    // #7 內文插入連結（markdown）
    const insertLink = () => {
        const url = window.prompt('連結網址（含 https://）：');
        if (!url) return;
        const text = window.prompt('顯示文字（可留空，用網址當文字）：') || url;
        const newContent = `${contentInput} [${text}](${url}) `;
        setContentInput(newContent);
        setForm((f) => ({ ...f, content: newContent }));
    };

    const buildTargets = () => {
        if (scope === 'all') return [{ target_type: 'all' }];
        return scopeValues.map((v) => ({ target_type: scope, target_value: String(v) }));
    };

    // 把一個本機 blob 網址真正上傳到 Cloudinary，回傳正式網址；不是待上傳的（已經是真網址）就原樣回傳
    const uploadPendingUrl = async (localUrl) => {
        const file = pendingFilesRef.current.get(localUrl);
        if (!file) return localUrl;
        const fd = new FormData();
        fd.append('files', file, file.name || 'image.jpg');
        const res = await authAPI.communicationBook.uploadPhoto(fd);
        const real = res.data?.urls?.[0] || res.data?.url;
        if (!real) throw new Error('上傳回應格式異常');
        URL.revokeObjectURL(localUrl);
        pendingFilesRef.current.delete(localUrl);
        return real;
    };

    const submit = async () => {
        if (!form.title.trim()) { toast({ title: '請輸入標題', status: 'warning' }); return; }
        if (scope !== 'all' && scopeValues.length === 0) { toast({ title: '請選擇發送對象', status: 'warning' }); return; }
        const status = publishMode === 'now' ? 'published' : (publishMode === 'scheduled' ? 'scheduled' : 'draft');
        if (status === 'scheduled' && !publishAt) { toast({ title: '預約發布需要時間', status: 'warning' }); return; }

        setSaving(true);
        try {
            // 找出封面 + 內文裡所有還沒真正上傳的本機圖片，送出前依序上傳
            const contentBlobUrls = [...new Set(contentInput.match(/blob:[^\s)]+/g) || [])];
            const coverIsPending = form.cover_image_url?.startsWith('blob:');
            const total = contentBlobUrls.length + (coverIsPending ? 1 : 0);
            let done = 0;

            let finalCoverUrl = form.cover_image_url;
            if (coverIsPending) {
                setSaveStatusText(`上傳圖片中 (${done + 1}/${total})`);
                finalCoverUrl = await uploadPendingUrl(form.cover_image_url);
                done += 1;
            }

            let finalContent = contentInput;
            for (const localUrl of contentBlobUrls) {
                setSaveStatusText(`上傳圖片中 (${done + 1}/${total})`);
                const real = await uploadPendingUrl(localUrl);
                finalContent = finalContent.split(localUrl).join(real);
                done += 1;
            }

            setSaveStatusText('儲存中...');
            const payload = {
                ...form,
                content: finalContent,
                cover_image_url: finalCoverUrl,
                expires_at: form.expires_at || null,
                status,
                publish_at: status === 'scheduled' ? publishAt : null,
                targets: buildTargets(),
            };
            if (isEdit) await authAPI.announcements.update(id, payload);
            else await authAPI.announcements.create(payload);
            toast({ title: isEdit ? '已更新' : '已建立', status: 'success', duration: 2000 });
            navigate('/announcements');
        } catch (err) {
            toast({ title: '儲存失敗', description: err?.response?.data?.detail || err?.message || '', status: 'error', duration: 4000 });
        } finally {
            setSaving(false);
            setSaveStatusText('');
        }
    };

    if (loading) return <Center py={20}><Spinner size="lg" /></Center>;

    const cover = resolveImageUrl(form.cover_image_url);
    const coverPos = form.cover_position || '50% 50%';

    return (
        <>
        <Box p={{ base: 4, md: 6 }} maxW="720px" mx="auto">
            <HStack mb={5} spacing={2}>
                <IconButton
                    aria-label="返回列表"
                    icon={<Icon as={FiChevronLeft} boxSize={6} />}
                    variant="ghost"
                    onClick={() => navigate('/announcements')}
                />
                <Heading size="lg">{isEdit ? '編輯公告' : '新增公告'}</Heading>
            </HStack>
            <VStack spacing={4} align="stretch" bg="white" p={6} borderRadius="lg" boxShadow="sm">
                <FormControl isRequired>
                    <FormLabel>標題</FormLabel>
                    <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </FormControl>
                <FormControl>
                    <FormLabel>摘要（列表 / Banner 顯示）</FormLabel>
                    <Input value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
                </FormControl>
                <FormControl>
                    <FormLabel>內容（支援 Markdown）</FormLabel>
                    <HStack mb={2} spacing={2}>
                        <Button as="label" size="xs" variant="outline" leftIcon={<Icon as={FiImage} />} cursor="pointer">
                            插入圖片
                            <input type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; insertBodyImage(f); }} />
                        </Button>
                        <Button size="xs" variant="outline" leftIcon={<Icon as={FiLink} />} onClick={insertLink}>插入連結</Button>
                    </HStack>
                    <Textarea rows={8} value={contentInput} onChange={(e) => setContentInput(e.target.value)} placeholder="支援 Markdown。圖片用上方「插入圖片」、連結用「插入連結」；家長端會渲染成圖片與可點連結。" />
                </FormControl>
                <FormControl>
                    <FormLabel>封面圖（選填）</FormLabel>
                    <Text fontSize="xs" color="gray.500" mb={2}>
                        上傳後會跳出裁切視窗：可拖曳移動、滾輪縮放，框內即為家長端 Banner 顯示範圍。
                    </Text>
                    <HStack mb={cover ? 3 : 0}>
                        <Button as="label" size="sm" cursor="pointer">
                            {cover ? '更換封面' : '上傳圖片'}
                            <input type="file" accept="image/*" hidden onChange={handleFileSelect} />
                        </Button>
                        {form.cover_image_url && (
                            <Button size="sm" variant="ghost" onClick={() => {
                                if (form.cover_image_url?.startsWith('blob:')) {
                                    URL.revokeObjectURL(form.cover_image_url);
                                    pendingFilesRef.current.delete(form.cover_image_url);
                                }
                                setForm({ ...form, cover_image_url: '' });
                            }}>移除</Button>
                        )}
                    </HStack>
                    {/* 家長端 Banner 即時預覽 */}
                    <Box mt={2}>
                        <Text fontSize="xs" color="gray.500" mb={1}>家長端首頁 Banner 預覽</Text>
                        <Box
                            position="relative" w="100%" maxW="358px" h="160px"
                            borderRadius="2xl" overflow="hidden" boxShadow="sm"
                            bg="gray.100"
                            bgGradient={cover ? undefined : 'linear(to-r, blue.400, brand.500)'}
                        >
                            {cover && (
                                <img
                                    src={cover}
                                    alt=""
                                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: coverPos }}
                                />
                            )}
                            <Box position="absolute" inset={0} bg={cover ? 'blackAlpha.500' : 'transparent'} />
                            {form.is_emergency && (
                                <Badge position="absolute" top={2} left={2} colorScheme="red" borderRadius="full" px={2} zIndex={1}>緊急</Badge>
                            )}
                            <VStack position="absolute" bottom={0} left={0} right={0} align="start" spacing={0.5} p={3} color="white" zIndex={1}>
                                <Text fontWeight="bold" fontSize="md" noOfLines={1}>{form.title || '（公告標題）'}</Text>
                                {form.summary && <Text fontSize="xs" opacity={0.9} noOfLines={1}>{form.summary}</Text>}
                            </VStack>
                        </Box>
                    </Box>
                </FormControl>

                <Divider />

                <FormControl>
                    <FormLabel>發送對象</FormLabel>
                    <Select value={scope} onChange={(e) => { setScope(e.target.value); setScopeValues([]); }}>
                        <option value="all">全校</option>
                        <option value="grade">指定年級</option>
                        <option value="class">指定班級</option>
                        <option value="department">指定部門</option>
                    </Select>
                </FormControl>
                {scope === 'grade' && (
                    <CheckboxGroup value={scopeValues} onChange={setScopeValues}>
                        <SimpleGrid columns={{ base: 3, md: 5 }} spacing={2}>
                            {GRADES.map((g) => <Checkbox key={g} value={g}>{g}年級</Checkbox>)}
                        </SimpleGrid>
                    </CheckboxGroup>
                )}
                {scope === 'department' && (
                    <CheckboxGroup value={scopeValues} onChange={setScopeValues}>
                        <HStack spacing={4}>{DEPTS.map((d) => <Checkbox key={d.value} value={d.value}>{d.label}</Checkbox>)}</HStack>
                    </CheckboxGroup>
                )}
                {scope === 'class' && (
                    <Box maxH="180px" overflowY="auto" borderWidth="1px" borderRadius="md" p={3}>
                        {classes.length === 0 ? (
                            <Text fontSize="sm" color="gray.400">沒有班級資料</Text>
                        ) : (
                            <CheckboxGroup value={scopeValues} onChange={setScopeValues}>
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                                    {classes.map((c) => <Checkbox key={c.id} value={String(c.id)}>{c.name}</Checkbox>)}
                                </SimpleGrid>
                            </CheckboxGroup>
                        )}
                    </Box>
                )}

                <Divider />

                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    <FormControl display="flex" alignItems="center">
                        <FormLabel mb={0}>緊急公告</FormLabel>
                        <Switch isChecked={form.is_emergency} onChange={(e) => setForm({ ...form, is_emergency: e.target.checked })} />
                    </FormControl>
                    <FormControl display="flex" alignItems="center">
                        <FormLabel mb={0}>置頂</FormLabel>
                        <Switch isChecked={form.is_pinned} onChange={(e) => setForm({ ...form, is_pinned: e.target.checked })} />
                    </FormControl>
                    <FormControl display="flex" alignItems="center">
                        <FormLabel mb={0}>發布時推播</FormLabel>
                        <Switch isChecked={form.push_on_publish} onChange={(e) => setForm({ ...form, push_on_publish: e.target.checked })} />
                    </FormControl>
                </SimpleGrid>

                <FormControl>
                    <FormLabel>到期自動下架（選填）</FormLabel>
                    <Input type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
                </FormControl>

                <Divider />

                <FormControl>
                    <FormLabel>發布方式</FormLabel>
                    <Select value={publishMode} onChange={(e) => setPublishMode(e.target.value)}>
                        <option value="draft">儲存為草稿</option>
                        <option value="now">立即發布</option>
                        <option value="scheduled">預約發布</option>
                    </Select>
                </FormControl>
                {publishMode === 'scheduled' && (
                    <FormControl isRequired>
                        <FormLabel>預約時間</FormLabel>
                        <Input type="datetime-local" value={publishAt} onChange={(e) => setPublishAt(e.target.value)} />
                    </FormControl>
                )}

                <HStack justify="flex-end" pt={2}>
                    <Button variant="ghost" onClick={() => navigate('/announcements')}>取消</Button>
                    <Button colorScheme="blue" isLoading={saving} onClick={submit}>{isEdit ? '更新' : '建立'}</Button>
                </HStack>
            </VStack>
        </Box>

        <PhonePreview
            title={form.title}
            summary={form.summary}
            content={debouncedContent}
            category={form.category}
            coverUrl={cover}
            coverPosition={coverPos}
            isEmergency={form.is_emergency}
        />

        {/* 封面裁切跳窗（拖曳移動 + 滾輪縮放，周圍變暗、框內為顯示範圍） */}
        <Modal isOpen={cropOpen} onClose={closeCrop} size={{ base: 'full', md: 'xl' }} isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>調整封面顯示範圍</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Box position="relative" w="100%" h={{ base: '55vh', md: '340px' }} bg="black" borderRadius="md" overflow="hidden">
                        {cropSrc && (
                            <Cropper
                                image={cropSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={COVER_ASPECT}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                            />
                        )}
                    </Box>
                    <HStack mt={4}>
                        <Text fontSize="sm" color="gray.500" w="48px">縮放</Text>
                        <Slider value={zoom} min={1} max={4} step={0.01} onChange={setZoom}>
                            <SliderTrack><SliderFilledTrack /></SliderTrack><SliderThumb />
                        </Slider>
                    </HStack>
                    <Text fontSize="xs" color="gray.400" mt={2}>拖曳移動、滾輪縮放；框內（亮處）即為家長端 Banner 顯示範圍。</Text>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={closeCrop}>取消</Button>
                    <Button colorScheme="blue" isLoading={cropping} onClick={applyCrop}>套用</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>

        <LoadingOverlay isLoading={saving} text={saveStatusText || '儲存中...'} />
        </>
    );
};

export default AnnouncementEditPage;
